import {
  Component,
  inject,
  computed,
  Output,
  EventEmitter,
  signal,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { catchError, forkJoin, map, of } from 'rxjs';
import { lastValueFrom } from 'rxjs';
import { ReferralQueryService } from '../../services/referral';
import { HospitalService } from '../../services/hospital.service';
import { ProgramService } from '../../services/program.service';
import { UserDisplayService } from '../../services/user-display.service';
import { ReferralFilter, ReferralStatus } from '@ics-referral-hub/shared-types';
import { getStatusDisplayText } from '../../shared/utils/status-severity.util';

interface FilterChip {
  key: string;
  label: string;
  value: any;
  type: 'text' | 'status' | 'date';
}

@Component({
  selector: 'app-filter-chips',
  standalone: true,
  imports: [CommonModule, ChipModule, ButtonModule],
  template: `
    <div class="filter-chips-container" *ngIf="activeFilters().length > 0">
      <div class="filter-chips-header">
        <span class="filter-label">Active Filters:</span>
        <button
          pButton
          icon="pi pi-times"
          label="Clear All"
          class="p-button-text p-button-sm clear-all-btn"
          (click)="clearAllFilters()"
        ></button>
      </div>
      <div class="filter-chips">
        <p-chip
          *ngFor="let chip of activeFilters()"
          [label]="chip.label"
          [removable]="true"
          (onRemove)="removeFilter(chip)"
          styleClass="filter-chip"
        ></p-chip>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .filter-chips-container {
        margin-bottom: 1rem;
        padding: 1rem;
        background-color: #f8f9fa;
        border-radius: 6px;
        border: 1px solid #e9ecef;
      }

      .filter-chips-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.75rem;
      }

      .filter-label {
        font-weight: 600;
        color: var(--text-color, #333);
        font-size: 0.9rem;
      }

      .clear-all-btn {
        color: var(--primary-color, #0b435b) !important;
        font-size: 0.8rem;
        padding: 0.25rem 0.5rem;
      }

      .filter-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      :host ::ng-deep {
        .filter-chip {
          background-color: var(--primary-color, #0b435b);
          color: white;
          font-size: 0.85rem;
        }

        .filter-chip .p-chip-text {
          color: white;
        }

        .filter-chip .p-chip-remove-icon {
          color: white;
          margin-left: 0.5rem;
        }

        .filter-chip .p-chip-remove-icon:hover {
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
        }
      }
    `,
  ],
})
export class FilterChipsComponent {
  @Output() filterRemoved = new EventEmitter<void>();
  @Output() allFiltersCleared = new EventEmitter<void>();

  private referralQueryService = inject(ReferralQueryService);
  private hospitalService = inject(HospitalService);
  private programService = inject(ProgramService);
  private userDisplayService = inject(UserDisplayService);
  private queryClient = inject(QueryClient);

  private hospitalNames = signal<Record<string, string>>({});
  private programNames = signal<Record<string, string>>({});
  private coordinatorNames = signal<Record<string, string>>({});
  private pendingProgramId: string | null = null;
  private readonly coordinatorFetchInFlight = new Set<string>();

  activeFilters = computed(() => {
    const filters = this.referralQueryService.filters();
    const hospitalNames = this.hospitalNames();
    const chips: FilterChip[] = [];

    // Text filters
    if (filters.referralId) {
      chips.push({
        key: 'referralId',
        label: `ID: ${filters.referralId}`,
        value: filters.referralId,
        type: 'text',
      });
    }

    if (filters.patientName) {
      chips.push({
        key: 'patientName',
        label: `Patient: ${filters.patientName}`,
        value: filters.patientName,
        type: 'text',
      });
    }

    const selectedHospitalIds =
      filters.hospitalIds?.length
        ? filters.hospitalIds
        : filters.hospitalId
          ? [filters.hospitalId]
          : [];

    selectedHospitalIds.forEach((hospitalId) => {
      chips.push({
        key: filters.hospitalIds?.length ? 'hospitalIds' : 'hospitalId',
        label: `Hospital: ${hospitalNames[hospitalId] || 'Loading...'}`,
        value: hospitalId,
        type: 'text',
      });
    });

    if (filters.programId) {
      chips.push({
        key: 'programId',
        label: `Program: ${this.programNames()[filters.programId] || 'Loading...'}`,
        value: filters.programId,
        type: 'text',
      });
    }

    if (filters.programType) {
      chips.push({
        key: 'programType',
        label: `Program Type: ${filters.programType}`,
        value: filters.programType,
        type: 'text',
      });
    }

    if (filters.assignedTo) {
      chips.push({
        key: 'assignedTo',
        label: `Assignment: ${
          this.coordinatorNames()[filters.assignedTo] || 'Loading...'
        }`,
        value: filters.assignedTo,
        type: 'text',
      });
    }

    // Status filters
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach((status) => {
        chips.push({
          key: 'status',
          label: `Status: ${getStatusDisplayText(status)}`,
          value: status,
          type: 'status',
        });
      });
    }

    if (filters.needsAttention) {
      chips.push({
        key: 'needsAttention',
        label: 'Needs attention',
        value: true,
        type: 'text',
      });
    }

    // Archived scope — show pill for active/archived only, not when viewing all referrals
    const effectiveArchivedFilter = this.getEffectiveArchivedFilter(filters);
    if (effectiveArchivedFilter === 'active') {
      chips.push({
        key: 'archivedFilter',
        label: 'Active only',
        value: 'active',
        type: 'text',
      });
    } else if (effectiveArchivedFilter === 'archived') {
      chips.push({
        key: 'archivedFilter',
        label: 'Archived only',
        value: 'archived',
        type: 'text',
      });
    }

    // Date filters
    if (filters.startDate) {
      const date = new Date(filters.startDate);
      chips.push({
        key: 'startDate',
        label: `From: ${date.toLocaleDateString()}`,
        value: filters.startDate,
        type: 'date',
      });
    }

    if (filters.endDate) {
      const date = new Date(filters.endDate);
      chips.push({
        key: 'endDate',
        label: `To: ${date.toLocaleDateString()}`,
        value: filters.endDate,
        type: 'date',
      });
    }

    return chips;
  });

  constructor() {
    // Only re-resolve labels when chip-relevant filters change — not on pagination
    // (limit/offset) updates from the dashboard, which previously re-fired coordinator
    // lookups and could spam /users/display.
    effect(() => {
      const filters = this.referralQueryService.filters();
      this.getLabelFilterKey(filters);
      untracked(() => this.resolveFilterLabels(filters));
    });
  }

  /** Stable key for filter fields that affect chip labels (excludes pagination). */
  private getLabelFilterKey(filters: ReferralFilter): string {
    const {
      limit,
      offset,
      page,
      sortBy,
      sortOrder,
      ...labelFilters
    } = filters;

    return JSON.stringify({
      ...labelFilters,
      startDate: filters.startDate
        ? new Date(filters.startDate).toISOString()
        : undefined,
      endDate: filters.endDate
        ? new Date(filters.endDate).toISOString()
        : undefined,
      status: filters.status?.length ? [...filters.status].sort() : undefined,
      hospitalIds: filters.hospitalIds?.length
        ? [...filters.hospitalIds].sort()
        : undefined,
    });
  }

  private resolveFilterLabels(filters: ReferralFilter): void {
    const selectedHospitalIds =
      filters.hospitalIds?.length
        ? filters.hospitalIds
        : filters.hospitalId
          ? [filters.hospitalId]
          : [];

    if (selectedHospitalIds.length > 0) {
      const missingHospitalIds = selectedHospitalIds.filter(
        (hospitalId) => !this.hospitalNames()[hospitalId],
      );

      if (missingHospitalIds.length > 0) {
        forkJoin(
          missingHospitalIds.map((hospitalId) =>
            this.hospitalService.getById(hospitalId).pipe(
              map((hospital) => [hospitalId, hospital.name] as const),
              catchError(() => of([hospitalId, 'Unknown Hospital'] as const)),
            ),
          ),
        ).subscribe((resolvedHospitals) => {
          this.hospitalNames.update((current) => ({
            ...current,
            ...Object.fromEntries(resolvedHospitals),
          }));
        });
      }
    } else {
      this.hospitalNames.set({});
    }

    const programId = filters.programId;
    if (programId) {
      const cachedProgramName = this.programNames()[programId];
      if (!cachedProgramName && this.pendingProgramId !== programId) {
        this.pendingProgramId = programId;
        this.programService.getById(programId).subscribe({
          next: (program) => {
            this.programNames.update((current) => ({
              ...current,
              [programId]: program.name,
            }));
            if (this.pendingProgramId === programId) {
              this.pendingProgramId = null;
            }
          },
          error: () => {
            this.programNames.update((current) => ({
              ...current,
              [programId]: 'Unknown Program',
            }));
            if (this.pendingProgramId === programId) {
              this.pendingProgramId = null;
            }
          },
        });
      }
    } else {
      this.pendingProgramId = null;
    }

    const coordinatorId = filters.assignedTo;
    if (coordinatorId) {
      void this.resolveCoordinatorName(coordinatorId);
    }
  }

  private async resolveCoordinatorName(coordinatorId: string): Promise<void> {
    if (this.coordinatorNames()[coordinatorId]) {
      return;
    }
    if (this.coordinatorFetchInFlight.has(coordinatorId)) {
      return;
    }

    this.coordinatorFetchInFlight.add(coordinatorId);
    try {
      const user = await this.queryClient.fetchQuery({
        queryKey: ['user-display', coordinatorId],
        queryFn: () =>
          lastValueFrom(this.userDisplayService.getUserDisplayInfo(coordinatorId)),
        staleTime: 30 * 60 * 1000,
      });
      this.coordinatorNames.update((current) => ({
        ...current,
        [coordinatorId]:
          user.displayName ||
          user.fullName ||
          user.email ||
          'Unknown coordinator',
      }));
    } catch {
      this.coordinatorNames.update((current) => ({
        ...current,
        [coordinatorId]: 'Unknown coordinator',
      }));
    } finally {
      this.coordinatorFetchInFlight.delete(coordinatorId);
    }
  }

  /** Matches referral-list query behavior for archived scope. */
  private getEffectiveArchivedFilter(
    filters: ReferralFilter,
  ): 'active' | 'archived' | 'all' {
    if (filters.archivedFilter === 'all') {
      return 'all';
    }
    if (filters.patientName && filters.archivedFilter !== 'active') {
      return 'all';
    }
    if (filters.archivedFilter === 'archived') {
      return 'archived';
    }
    return 'active';
  }

  removeFilter(chip: FilterChip): void {
    const currentFilters = { ...this.referralQueryService.filters() };

    if (chip.type === 'status') {
      // Remove specific status from array
      if (currentFilters.status) {
        currentFilters.status = currentFilters.status.filter(
          (s: ReferralStatus) => s !== chip.value,
        );
        if (currentFilters.status.length === 0) {
          delete currentFilters.status;
        }
      }
    } else if (chip.key === 'hospitalIds') {
      const remainingHospitalIds =
        currentFilters.hospitalIds?.filter(
          (hospitalId: string) => hospitalId !== chip.value,
        ) ?? [];

      if (remainingHospitalIds.length > 0) {
        currentFilters.hospitalIds = remainingHospitalIds;
      } else {
        delete currentFilters.hospitalIds;
      }
    } else if (chip.key === 'archivedFilter') {
      if (chip.value === 'active') {
        // Expand scope to all referrals (active + archived); no pill for "all"
        currentFilters.archivedFilter = 'all';
      } else if (chip.value === 'archived') {
        delete currentFilters.archivedFilter;
      }
    } else {
      // Remove other filter types
      delete (currentFilters as any)[chip.key];
    }

    // Preserve pagination settings
    const { limit, offset } = this.referralQueryService.filters();
    this.referralQueryService.setFilters({
      ...currentFilters,
      limit,
      offset,
    });

    this.filterRemoved.emit();
  }

  clearAllFilters(): void {
    // Keep only pagination settings
    const { limit, offset } = this.referralQueryService.filters();
    this.referralQueryService.setFilters({
      limit,
      offset,
    });

    this.allFiltersCleared.emit();
  }
}
